import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileCard } from '@shared/components/files';
import { TooltipProvider } from '@shared/components/ui/Tooltip';
import { fileExtension, isBrowserPreviewableImage } from '@shared/lib/file-format';

import { getFileBlob } from '../../data/file-blobs';
import { useFileObjectUrls } from '../../hooks/use-file-object-urls';
import { useTrackVaultFiles, useVaultFileLink } from '../../hooks/use-vault-file-link';
import type { AttachedFile } from '../../types';

import { AudioPlayerCard } from './AudioPlayerCard';
import { ImageGrid } from './ImageGrid';
import { ImagePreview } from './ImagePreview';
import { ImageViewer } from './ImageViewer';
import { isAudioName, type SentImage } from './file-meta';

interface SentFilesProps {
  files: AttachedFile[];
  sentAt: number;
}

interface ViewerState {
  open: boolean;
  index: number;
  session: number;
}

export function SentFiles({ files, sentAt }: SentFilesProps) {
  const { t } = useTranslation('ai-cio');
  const filesWithBytes = files.filter((file) => getFileBlob(file.id) !== undefined);
  const urls = useFileObjectUrls(filesWithBytes.map((file) => file.id));
  const [viewer, setViewer] = useState<ViewerState>({ open: false, index: 0, session: 0 });
  const openerRef = useRef<HTMLElement | null>(null);

  const imageFiles = filesWithBytes.filter((file) =>
    isBrowserPreviewableImage(fileExtension(file.name)),
  );
  const audioFiles = files.filter((file) => isAudioName(file.name));
  const documentFiles = files.filter(
    (file) => !imageFiles.includes(file) && !audioFiles.includes(file),
  );
  const cardFiles = [...documentFiles, ...audioFiles];
  const images: SentImage[] = imageFiles.map(({ id, name, size, vaultFileId }) => ({
    id,
    name,
    size,
    url: urls.get(id),
    vaultFileId,
  }));
  const [firstImage] = images;
  useTrackVaultFiles(images.map((image) => image.vaultFileId));

  function openViewer(index: number, opener: HTMLElement) {
    openerRef.current = opener;
    setViewer((current) => ({ open: true, index, session: current.session + 1 }));
  }

  return (
    <TooltipProvider>
      <div
        role="group"
        aria-label={t('sentFiles.filesLabel', 'Attached files')}
        className="flex w-full flex-col items-end gap-2"
      >
        {images.length === 1 && firstImage && (
          <ImagePreview
            image={firstImage}
            onOpen={(opener) => {
              openViewer(0, opener);
            }}
          />
        )}
        {images.length > 1 && <ImageGrid images={images} onOpen={openViewer} />}
        {cardFiles.length > 0 && (
          <ul className="flex w-full flex-col items-end gap-2">
            {cardFiles.map((file) => {
              const url = urls.get(file.id);
              return (
                <li key={file.id} className="w-80 max-w-full">
                  {url && audioFiles.includes(file) ? (
                    <SentAudioCard file={file} src={url} />
                  ) : (
                    <SentFileCard file={file} url={url} />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {images.length > 0 && (
        <ImageViewer
          key={viewer.session}
          images={images}
          open={viewer.open}
          startIndex={viewer.index}
          sentAt={sentAt}
          returnFocusRef={openerRef}
          onOpenChange={(open) => {
            if (!open) setViewer((current) => ({ ...current, open: false }));
          }}
        />
      )}
    </TooltipProvider>
  );
}

function SentFileCard({ file, url }: { file: AttachedFile; url?: string }) {
  const link = useVaultFileLink(file.vaultFileId);
  if (link?.removed) {
    return (
      <FileCard
        name={file.name}
        state="unavailable"
        sizeBytes={file.size}
        announceUnavailable={link.removedOnOpen}
      />
    );
  }
  return (
    <FileCard
      name={file.name}
      state="sent"
      sizeBytes={file.size}
      onOpen={
        link
          ? () => {
              void link.open();
            }
          : undefined
      }
      downloadHref={url}
      downloadName={url ? file.name : undefined}
      onDownload={
        !url && link?.canDownload
          ? () => {
              link.download();
            }
          : undefined
      }
    />
  );
}

function SentAudioCard({ file, src }: { file: AttachedFile; src: string }) {
  const link = useVaultFileLink(file.vaultFileId);
  const ref = useRef<HTMLDivElement>(null);
  const removedOnOpen = link?.removedOnOpen ?? false;

  useLayoutEffect(() => {
    const active = document.activeElement;
    if (!removedOnOpen || (active && active !== document.body)) return;
    ref.current?.querySelector<HTMLElement>('[tabindex]')?.focus();
  }, [removedOnOpen]);

  return (
    <div ref={ref}>
      {link?.removed ? (
        <FileCard
          name={file.name}
          state="unavailable"
          sizeBytes={file.size}
          announceUnavailable={link.removedOnOpen}
        />
      ) : (
        <AudioPlayerCard
          name={file.name}
          sizeBytes={file.size}
          src={src}
          downloadHref={src}
          downloadName={file.name}
          onOpen={
            link
              ? () => {
                  void link.open();
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
